import React, { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { getRequestWithToken } from "../../utils/ApiRequests";

const apiBase = "../v3/admin";
const nameOf = (item, keys) => keys.map((key) => item?.[key]).find(Boolean) || "";
const aliasesOf = (role) => (role?.aliases?.en || []).map((value) => String(value).trim()).filter(Boolean);

/** Selects an existing canonical Candidate Family → Group → Role only. */
const CandidateRoleSelector = ({ value = null, onChange, disabled = false, required = true, compact = false }) => {
  const [families, setFamilies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState({ families: false, groups: false, roles: false });
  const [error, setError] = useState("");
  const requestRef = useRef(0);
  const selectedFamily = value?.family || null;
  const selectedGroup = value?.group || null;
  const selectedRole = value?.role || null;

  const emit = (next) => onChange?.({ family: null, group: null, role: null, ...next });

  useEffect(() => {
    let active = true;
    setLoading((current) => ({ ...current, families: true }));
    getRequestWithToken(`${apiBase}/families`)
      .then((res) => { if (active) setFamilies(res.data?.data || []); })
      .catch(() => { if (active) setError("Unable to load Candidate Families. Please retry."); })
      .finally(() => { if (active) setLoading((current) => ({ ...current, families: false })); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedFamily?._id) { setGroups([]); return undefined; }
    const requestId = ++requestRef.current;
    setLoading((current) => ({ ...current, groups: true }));
    getRequestWithToken(`${apiBase}/families/${selectedFamily._id}/groups`)
      .then((res) => { if (requestId === requestRef.current) setGroups(res.data?.data || []); })
      .catch(() => { if (requestId === requestRef.current) setError("Unable to load Role Groups. Please retry."); })
      .finally(() => { if (requestId === requestRef.current) setLoading((current) => ({ ...current, groups: false })); });
    return undefined;
  }, [selectedFamily?._id]);

  useEffect(() => {
    if (!selectedGroup?._id) { setRoles([]); return undefined; }
    const requestId = ++requestRef.current;
    setLoading((current) => ({ ...current, roles: true }));
    getRequestWithToken(`${apiBase}/groups/${selectedGroup._id}/roles`)
      .then((res) => { if (requestId === requestRef.current) setRoles(res.data?.data || []); })
      .catch(() => { if (requestId === requestRef.current) setError("Unable to load Candidate Roles. Please retry."); })
      .finally(() => { if (requestId === requestRef.current) setLoading((current) => ({ ...current, roles: false })); });
    return undefined;
  }, [selectedGroup?._id]);

  return (
    <Box display="grid" gap={compact ? 1 : 1.5}>
      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
      <Autocomplete
        options={families}
        value={selectedFamily}
        loading={loading.families}
        disabled={disabled}
        isOptionEqualToValue={(option, selected) => String(option?._id) === String(selected?._id)}
        getOptionLabel={(item) => nameOf(item, ["family_ui_label", "family_name", "family_id"])}
        onChange={(_, family) => emit({ family })}
        renderInput={(params) => <TextField {...params} required={required} label="Candidate Family" InputProps={{ ...params.InputProps, endAdornment: <>{loading.families ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
      />
      <Autocomplete
        options={groups}
        value={selectedGroup}
        loading={loading.groups}
        disabled={disabled || !selectedFamily}
        isOptionEqualToValue={(option, selected) => String(option?._id) === String(selected?._id)}
        getOptionLabel={(item) => nameOf(item, ["group_ui_label", "group_name", "group_id"])}
        onChange={(_, group) => emit({ family: selectedFamily, group })}
        renderInput={(params) => <TextField {...params} required={required} label="Candidate Role Group" />}
      />
      <Autocomplete
        options={roles}
        value={selectedRole}
        loading={loading.roles}
        disabled={disabled || !selectedGroup}
        isOptionEqualToValue={(option, selected) => String(option?._id) === String(selected?._id)}
        getOptionLabel={(item) => nameOf(item, ["role_ui_label", "role_name", "role_id"])}
        getOptionDisabled={(item) => aliasesOf(item).length === 0}
        onChange={(_, role) => emit({ family: selectedFamily, group: selectedGroup, role })}
        renderOption={(props, item) => (
          <li {...props}>
            <Box>
              <Typography variant="body2">{nameOf(item, ["role_ui_label", "role_name", "role_id"])}</Typography>
              <Typography variant="caption" color={aliasesOf(item).length ? "text.secondary" : "error"}>
                {aliasesOf(item).length ? `Aliases: ${aliasesOf(item).join(", ")}` : "Add an English alias before this role can be mapped"}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => <TextField {...params} required={required} label="Candidate Role" helperText={selectedRole ? `Allowed levels: ${(selectedRole.allowed_level_ids || []).join(", ") || "None configured"}` : "Select a role with at least one English alias"} />}
      />
    </Box>
  );
};

export default CandidateRoleSelector;
